// src/App.jsx
import { Box } from "@mui/joy";
import { useMediaQuery, useTheme } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import { useContext, useEffect, useState } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { AuthContext } from "./context/AuthContext"; // NOTE: we assume AuthProvider wraps <App /> in index.jsx
import Auth from "./pages/Auth";
import Budgets from "./pages/Budgets";
import Charts from "./pages/Charts";
import Dashboard from "./pages/Dashboard";
import Navbar from "./pages/Nav";
import Reports from "./pages/Reports";
import SideBar from "./pages/SideBar";
import Transactions from "./pages/Transactions";
import logo from "../src/assets/fintrack-high-resolution-logo-transparent.svg"
// Private route
const PrivateRoute = ({ children }) => {
  const { user } = useContext(AuthContext);
  return user ? children : <Navigate to="/login" replace />;
};

// Page transition wrapper
const PageTransition = ({ children }) => {
  const variants = {
    initial: { opacity: 0, y: 8, scale: 0.995 },
    animate: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.28, ease: "easeOut" },
    },
    exit: {
      opacity: 0,
      y: -6,
      scale: 0.995,
      transition: { duration: 0.18, ease: "easeIn" },
    },
  };

  return (
    <motion.div
      variants={variants}
      initial="initial"
      animate="animate"
      exit="exit"
      style={{ width: "100%", height: "100%" }}
      key={
        typeof children === "object"
          ? (children.props?.routeKey ?? undefined)
          : undefined
      }
    >
      {children}
    </motion.div>
  );
};

export default function App() {
  const { user, logout } = useContext(AuthContext);
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const isMedium = useMediaQuery(theme.breakpoints.down("md"));

  const [drawerIsOpen, setDrawerIsOpen] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [budgets, setBudgets] = useState([]);

  const navigate = useNavigate();
  const location = useLocation();

  // data fetch on mount
  useEffect(() => {
    let cancelled = false;

    const fetchAll = async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = token
          ? {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            }
          : { "Content-Type": "application/json" };

        const API_URL = process.env.REACT_APP_API_URL;

        const [txRes, catRes, budRes] = await Promise.all([
          fetch(`${API_URL}/api/transactions`, { method: "GET", headers }),
          fetch(`${API_URL}/api/categories`, { headers }),
          fetch(`${API_URL}/api/budgets`, { headers }),
        ]);

        const [txData, catData, budData] = await Promise.all([
          txRes.ok ? txRes.json() : Promise.resolve([]),
          catRes.ok ? catRes.json() : Promise.resolve([]),
          budRes.ok ? budRes.json() : Promise.resolve([]),
        ]);

        if (cancelled) return;

        const sortedTx = Array.isArray(txData)
          ? [...txData].sort((a, b) => new Date(b.date) - new Date(a.date))
          : [];

        setTransactions(sortedTx);
        setCategories(Array.isArray(catData) ? catData : []);
        setBudgets(Array.isArray(budData) ? budData : []);

        // console.log("App fetched categories:", Array.isArray(catData) ? catData : []);
      } catch (err) {
        console.error("Failed to fetch initial data in App:", err);
      }
    };

    fetchAll();

    return () => {
      cancelled = true;
    };
  }, []); // run once on mount

  // React to auth:logout broadcasts and storage changes (other tabs)
  useEffect(() => {
    const onAuthLogout = (e) => {
      // Ensure user context cleared (AuthProvider also clears it) then navigate to login
      // call logout() to be safe (it clears context). AuthProvider already listens but calling is idempotent.
      try {
        logout();
      } catch (err) {
        // ignore
      }
      navigate("/login", { replace: true });
    };

    const onStorage = (e) => {
      if (e.key === "token" && e.newValue === null) {
        try {
          logout();
        } catch (err) {}
        navigate("/login", { replace: true });
      }
    };

    window.addEventListener("auth:logout", onAuthLogout);
    window.addEventListener("storage", onStorage);

    return () => {
      window.removeEventListener("auth:logout", onAuthLogout);
      window.removeEventListener("storage", onStorage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, logout]);

  const handleAddTransaction = (transaction) => {
    setTransactions((prev) => [transaction, ...prev]);
  };

  return (
    <>
      {/* Navbar always mounted — inside Navbar you can also use AuthContext to render user-specific UI */}
      <Navbar drawerIsOpen={drawerIsOpen} />

      <Box sx={{ display: "flex", minHeight: "100vh" }}>
        {/* Show SideBar only when user present */}
        {user && <SideBar user={user} setDrawerIsOpen={setDrawerIsOpen} />}

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            marginLeft: user && !isSmall && !isMedium ? "calc(5% + 16px)" : 0,
            padding: { xs: 2, sm: 3 },
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          <AnimatePresence mode="wait" initial={false}>
            <Routes location={location} key={location.pathname}>
              <Route
                path="/login"
                element={
                  <PageTransition>
                    <Auth />
                  </PageTransition>
                }
              />
              <Route
                path="/register"
                element={
                  <PageTransition>
                    <Auth />
                  </PageTransition>
                }
              />

              <Route path="/" element={<Navigate to="/dashboard" />} />

              <Route
                path="/dashboard"
                element={
                  <PageTransition>
                    <PrivateRoute>
                      <Dashboard
                        transactions={transactions}
                        liftTransactions={setTransactions}
                        categories1={categories}
                        budgets={budgets}
                        onAddTransaction={handleAddTransaction}
                        setCategories={setCategories}
                        setBudgets={setBudgets}
                      />
                    </PrivateRoute>
                  </PageTransition>
                }
              />

              <Route
                path="/transactions"
                element={
                  <PageTransition>
                    <PrivateRoute>
                      <Transactions
                        onAddTransaction={handleAddTransaction}
                        categories={categories}
                        transactions={transactions}
                      />
                    </PrivateRoute>
                  </PageTransition>
                }
              />

              <Route
                path="/budgets"
                element={
                  <PageTransition>
                    <PrivateRoute>
                      <Budgets budgets={budgets} categories={categories} />
                    </PrivateRoute>
                  </PageTransition>
                }
              />

              <Route
                path="/reports"
                element={
                  <PageTransition>
                    <PrivateRoute>
                      <Reports
                        transactions={transactions}
                        categories={categories}
                      />
                    </PrivateRoute>
                  </PageTransition>
                }
              />

              <Route
                path="/charts"
                element={
                  <PageTransition>
                    <PrivateRoute>
                      <Charts
                        transactions={transactions}
                        categories={categories}
                      />
                    </PrivateRoute>
                  </PageTransition>
                }
              />
            </Routes>
          </AnimatePresence>
        </Box>
      </Box>
    </>
  );
}
